import React, { useState, useEffect } from "react";
import { makeStyles, useTheme, Dropdown } from "@seahorse/ui";
import {
  fromZonedDateTimeInputValue,
  toUtcDateTimeValue,
  getDateTimeInputZoneLabel,
  getExecutionTimestampBounds,
  toZonedDateTimeInputValue,
} from "../shared/dateTimeInput";
import { useDisplayTimeZone } from "../../Utils/displayTimeZone";

type TradeAnalysisLandingField =
  | "currencyPair"
  | "tradeType"
  | "executionTimestamp"
  | "executionSpotRate"
  | "executionForwardPoints"
  | "expirationTimestamp";

type TradeAnalysisLandingPrefill = {
  currencyPair?: string;
  tradeType?: "BUY" | "SELL" | "";
  executionTimestamp?: string;
  executionSpotRate?: number | null;
  executionForwardPoints?: number | null;
  expirationTimestamp?: string;
};

interface TradeAnalysisLandingPageProps {
  currencyList: string[];
  onSubmit: (trade: {
    currencyPair: string;
    tradeType: "BUY" | "SELL";
    executionTimestamp: string;
    executionSpotRate: number | null;
    executionForwardPoints: number | null;
    expirationTimestamp: string;
  }) => void;
  initialTrade?: TradeAnalysisLandingPrefill;
  notice?: string;
  requiredFields?: TradeAnalysisLandingField[];
}

const TRADE_TYPE_OPTIONS = [
  { label: "BUY", value: "BUY" },
  { label: "SELL", value: "SELL" },
];

const TradeAnalysisLandingPage: React.FC<TradeAnalysisLandingPageProps> = ({
  currencyList,
  onSubmit,
  initialTrade,
  notice,
  requiredFields = [],
}) => {
  const theme = useTheme();

  const [currencyPair, setCurrencyPair] = useState("");
  const [tradeType, setTradeType] = useState<"BUY" | "SELL" | "">("");
  const [executionTimestamp, setExecutionTimestamp] = useState("");
  const [executionSpotRate, setExecutionSpotRate] = useState("");
  const [executionForwardPoints, setExecutionForwardPoints] = useState("");
  const [expirationTimestamp, setExpirationTimestamp] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Timestamps are held in UTC; the inputs show the display zone.
  const { timeZone } = useDisplayTimeZone();
  const executionInput = toZonedDateTimeInputValue(executionTimestamp, timeZone);
  const expirationInput = toZonedDateTimeInputValue(
    expirationTimestamp,
    timeZone
  );
  const { minExecutionTimestamp, maxExecutionTimestamp } =
    getExecutionTimestampBounds(timeZone);

  const [dropdownItems, setDropdownItems] = useState<
    { label: string; value: string }[]
  >([]);

  useEffect(() => {
    if (currencyList?.length) {
      setDropdownItems(
        [...currencyList]
          .sort((a, b) => a.localeCompare(b))
          .map((currency) => ({ label: currency, value: currency }))
      );
    }
  }, [currencyList]);

  useEffect(() => {
    if (!initialTrade) return;

    setCurrencyPair(initialTrade.currencyPair ?? "");
    setTradeType(initialTrade.tradeType ?? "");
    setExecutionTimestamp(initialTrade.executionTimestamp ?? "");
    setExecutionSpotRate(
      initialTrade.executionSpotRate == null
        ? ""
        : String(initialTrade.executionSpotRate)
    );
    setExecutionForwardPoints(
      initialTrade.executionForwardPoints == null
        ? ""
        : String(initialTrade.executionForwardPoints)
    );
    setExpirationTimestamp(initialTrade.expirationTimestamp ?? "");
  }, [initialTrade]);

  const requiresField = (field: TradeAnalysisLandingField) =>
    requiredFields.includes(field);

  const validate = () => {
    const next: Record<string, string> = {};
    if (!currencyPair) next.currencyPair = "Currency pair is required";
    if (!executionInput) {
      next.executionTimestamp = "Execution timestamp is required";
    } else if (
      executionInput < minExecutionTimestamp ||
      executionInput > maxExecutionTimestamp
    ) {
      next.executionTimestamp =
        "Execution timestamp must be within the last 2 months and not in the future";
    }
    if (requiresField("executionSpotRate") && !executionSpotRate.trim()) {
      next.executionSpotRate = "Execution spot rate is required";
    } else if (
      executionSpotRate.trim() &&
      !Number.isFinite(Number(executionSpotRate))
    ) {
      next.executionSpotRate = "Execution spot rate must be a valid number";
    }
    if (
      requiresField("executionForwardPoints") &&
      !executionForwardPoints.trim()
    ) {
      next.executionForwardPoints = "Execution forward points are required";
    } else if (
      executionForwardPoints.trim() &&
      !Number.isFinite(Number(executionForwardPoints))
    ) {
      next.executionForwardPoints =
        "Execution forward points must be a valid number";
    }
    if (requiresField("expirationTimestamp") && !expirationInput) {
      next.expirationTimestamp = "Expiration date is required";
    }
    if (expirationInput && executionInput && expirationInput < executionInput) {
      next.expirationTimestamp =
        "Expiration date must be greater than or equal to execution timestamp";
    }
    if (!tradeType) next.tradeType = "Trade type is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const parsedRate = Number(executionSpotRate);
    const parsedFwd = Number(executionForwardPoints);

    onSubmit({
      currencyPair,
      tradeType: tradeType as "BUY" | "SELL",
      executionTimestamp: toUtcDateTimeValue(executionTimestamp),
      executionSpotRate: Number.isFinite(parsedRate) ? parsedRate : null,
      executionForwardPoints: Number.isFinite(parsedFwd) ? parsedFwd : null,
      expirationTimestamp: toUtcDateTimeValue(expirationTimestamp),
    });
  };

  const useStyles = makeStyles({
    wrapper: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      width: "100%",
      backgroundColor: theme.globallink.colors.DarkGrey[500],
      padding: 24,
    },
    card: {
      width: "100%",
      maxWidth: 480,
      borderRadius: 12,
      border: "1px solid rgba(255,255,255,0.08)",
      background: "rgba(255,255,255,0.04)",
      padding: "32px 28px",
      display: "flex",
      flexDirection: "column",
      gap: 20,
    },
    header: {
      textAlign: "center" as const,
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },
    title: {
      color: "#E0E0E0",
      fontSize: 20,
      fontWeight: 700,
      margin: 0,
    },
    subtitle: {
      color: "#E0E0E0",
      fontSize: 13,
      opacity: 0.6,
      margin: 0,
    },
    notice: {
      borderRadius: 10,
      border: "1px solid rgba(93, 173, 226, 0.4)",
      background: "rgba(93, 173, 226, 0.12)",
      color: "#D6EAF8",
      padding: "12px 14px",
      fontSize: 12,
      lineHeight: "18px",
    },
    fieldGroup: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },
    label: {
      color: "#E0E0E0",
      fontSize: 13,
      fontWeight: 600,
      opacity: 0.9,
    },
    input: {
      width: "100%",
      padding: "10px 12px",
      borderRadius: 8,
      border: "1px solid rgba(255,255,255,0.12)",
      background: "rgba(255,255,255,0.06)",
      color: "#E0E0E0",
      fontSize: 14,
      outline: "none",
      "&::placeholder": {
        color: "rgba(224,224,224,0.35)",
      },
    },
    error: {
      color: "#ff6b6b",
      fontSize: 12,
      margin: 0,
    },
    button: {
      marginTop: 8,
      cursor: "pointer",
      padding: "12px 0",
      borderRadius: 10,
      border: "none",
      background: theme.globallink.colors.Blue[500],
      color: "#FFFFFF",
      fontSize: 15,
      fontWeight: 700,
      transition: "opacity 0.15s ease",
      "&:hover": {
        opacity: 0.9,
      },
    },
    optionalHint: {
      color: "#E0E0E0",
      fontSize: 11,
      opacity: 0.45,
      fontWeight: 400,
    },
  });

  const classes = useStyles();

  return (
    <div className={classes.wrapper}>
      <div className={classes.card}>
        <div className={classes.header}>
          <p className={classes.title}>EMAlpha Trade Analysis</p>
          <p className={classes.subtitle}>
            Enter your trade details to begin the analysis
          </p>
        </div>
        {notice && <div className={classes.notice}>{notice}</div>}

        {/* Currency Pair */}
        <div className={classes.fieldGroup}>
          <span className={classes.label}>Currency Pair</span>
          <Dropdown
            items={dropdownItems}
            value={currencyPair}
            onChange={(value: any) => {
              setCurrencyPair(value);
              setErrors((prev) => ({ ...prev, currencyPair: "" }));
            }}
            placeholder="Select currency pair"
            fullWidth
          />
          {errors.currencyPair && (
            <p className={classes.error}>{errors.currencyPair}</p>
          )}
        </div>

        {/* Trade Type */}
        <div className={classes.fieldGroup}>
          <span className={classes.label}>Trade Type</span>
          <Dropdown
            items={TRADE_TYPE_OPTIONS}
            value={tradeType}
            onChange={(value: any) => {
              setTradeType(value);
              setErrors((prev) => ({ ...prev, tradeType: "" }));
            }}
            placeholder="Select trade type"
            fullWidth
          />
          {errors.tradeType && (
            <p className={classes.error}>{errors.tradeType}</p>
          )}
        </div>

        {/* Execution Timestamp */}
        <div className={classes.fieldGroup}>
          <span className={classes.label}>
            Execution Timestamp ({getDateTimeInputZoneLabel(executionTimestamp, timeZone)})
          </span>
          <input
            className={classes.input}
            type="datetime-local"
            step={60}
            min={minExecutionTimestamp}
            max={maxExecutionTimestamp}
            value={executionInput}
            onChange={(e) => {
              setExecutionTimestamp(
                fromZonedDateTimeInputValue(e.target.value, timeZone)
              );
              setErrors((prev) => ({
                ...prev,
                executionTimestamp: "",
                expirationTimestamp: "",
              }));
            }}
          />
          {errors.executionTimestamp && (
            <p className={classes.error}>{errors.executionTimestamp}</p>
          )}
        </div>

        {/* Execution Spot Rate */}
        <div className={classes.fieldGroup}>
          <span className={classes.label}>
            Execution Spot Rate{" "}
            <span className={classes.optionalHint}>
              {requiresField("executionSpotRate") ? "(required)" : "(optional)"}
            </span>
          </span>
          <input
            className={classes.input}
            value={executionSpotRate}
            onChange={(e) => {
              setExecutionSpotRate(e.target.value);
              setErrors((prev) => ({ ...prev, executionSpotRate: "" }));
            }}
            placeholder="e.g. 157.96"
            inputMode="decimal"
          />
          {errors.executionSpotRate && (
            <p className={classes.error}>{errors.executionSpotRate}</p>
          )}
        </div>

        {/* Execution Forward Points */}
        <div className={classes.fieldGroup}>
          <span className={classes.label}>
            Execution Forward Points{" "}
            <span className={classes.optionalHint}>
              {requiresField("executionForwardPoints")
                ? "(required)"
                : "(optional)"}
            </span>
          </span>
          <input
            className={classes.input}
            value={executionForwardPoints}
            onChange={(e) => {
              setExecutionForwardPoints(e.target.value);
              setErrors((prev) => ({ ...prev, executionForwardPoints: "" }));
            }}
            placeholder="e.g. -0.0002"
            inputMode="decimal"
          />
          {errors.executionForwardPoints && (
            <p className={classes.error}>{errors.executionForwardPoints}</p>
          )}
        </div>

        {/* Expiration Timestamp */}
        <div className={classes.fieldGroup}>
          <span className={classes.label}>
            Expiration Date ({getDateTimeInputZoneLabel(expirationTimestamp, timeZone)}){" "}
            <span className={classes.optionalHint}>
              {requiresField("expirationTimestamp") ? "(required)" : "(optional)"}
            </span>
          </span>
          <input
            className={classes.input}
            type="datetime-local"
            step={60}
            min={executionInput || undefined}
            value={expirationInput}
            onChange={(e) => {
              setExpirationTimestamp(
                fromZonedDateTimeInputValue(e.target.value, timeZone)
              );
              setErrors((prev) => ({ ...prev, expirationTimestamp: "" }));
            }}
          />
          {errors.expirationTimestamp && (
            <p className={classes.error}>{errors.expirationTimestamp}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="button"
          className={classes.button}
          onClick={handleSubmit}
        >
          Analyze Trade
        </button>
      </div>
    </div>
  );
};

export default TradeAnalysisLandingPage;
